import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { get } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetBusinessUnitsQuery } from 'api/business-units/businessUnits'
import AccountBalanceChip from 'components/common/chip/AccountBalanceChip'
import AccountCreditChip from 'components/common/chip/AccountCreditChip'
import DeactivationPendingChip from 'components/common/chip/DeactivationPendingChip'
import SuspensionChip from 'components/common/chip/SuspensionChip'
import WorkOrdersCreatingChip from 'components/common/chip/WorkOrdersCreatingChip'
import VacationHoldChipAccountHeader from 'components/customer-details/content/holds/VacationHoldChipAccountHeader'
import { getSuspensionReasonLabel } from 'components/customer-details/content/suspensions/settings'
import WithSuspensionPopover from 'components/customer-details/content/suspensions/WithSuspensionPopover'
import { getBalanceChipColor } from 'components/customers/accounts/settings'
import InteractiveTagsList from 'components/tags/InteractiveTagsList'
import { selectBalance } from 'data/customer/billingDetailsSelector'
import { getCustomerDetails } from 'middleware/actions/customers'

const CustomerInfo = () => {
  const dispatch = useDispatch()
  const customerDetails = useSelector(state => get(state, 'CustomersReducer.customerDetails', null))
  const accountId = get(customerDetails, 'id', '')
  const accountName = get(customerDetails, 'accountName', '')
  const accountNumber = get(customerDetails, 'accountNumber', '')
  const billingProfile = get(customerDetails, 'accountBilling.billingProfile.profile', '')
  const isAccountSuspended = get(customerDetails, 'isSuspended', false)
  const deactivationStatus = get(customerDetails, 'deactivationStatus')
  const reactivationInProgressSince = get(customerDetails, 'reactivationInProgressSince')
  const suspensionDescription = get(customerDetails, 'suspensionDescription')
  const suspensionType = get(customerDetails, 'suspensionType')
  const balanceDetails = get(customerDetails, 'balanceDetails', {})
  const tags = get(customerDetails, 'tags', [])
  const customerCreditBalanceCents = get(customerDetails, 'customerCreditBalanceCents', 0)
  const balanceCents = selectBalance(customerDetails)
  const balanceChipColor = useMemo(() => getBalanceChipColor(balanceDetails), [balanceDetails])
  const businessUnitId = get(customerDetails, 'businessUnitId')
  const { data: businessUnits = [] } = useGetBusinessUnitsQuery(undefined, { skip: !businessUnitId })
  const businessUnitName = useMemo(() => businessUnits.find(unit => unit.id === businessUnitId)?.name, [businessUnits, businessUnitId])

  const handleTagsSave = useCallback(
    (data, closeCallback) => {
      closeCallback()
      dispatch(getCustomerDetails({ accountId }))
    },
    [dispatch, accountId]
  )

  return (
    <Grid container direction="column" ml={4} mt={1}>
      <Grid>
        <Grid container alignItems="end" columnSpacing={2}>
          <Grid size="auto">
            <Typography fontWeight={600} variant="h4">
              {accountName}
            </Typography>
          </Grid>
          <Grid size="grow">
            <Stack alignItems="center" columnGap={1} direction="row" flexWrap="nowrap">
              <VacationHoldChipAccountHeader />
              <DeactivationPendingChip status={deactivationStatus} />
              <WorkOrdersCreatingChip since={reactivationInProgressSince} />
              {isAccountSuspended && (
                <WithSuspensionPopover suspensionDescription={suspensionDescription} suspensionType={suspensionType}>
                  <SuspensionChip label={getSuspensionReasonLabel(suspensionType)} />
                </WithSuspensionPopover>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid container alignItems="center" gap={1} mt={1}>
          <Grid>
            <AccountBalanceChip balance={balanceCents} color={balanceChipColor} />
          </Grid>
          <Grid>
            <AccountCreditChip creditCents={customerCreditBalanceCents} />
          </Grid>
          <Grid>
            <Typography color="textSecondary" fontWeight={500}>
              {`# ${accountNumber}`}
            </Typography>
          </Grid>
          <Grid>
            <Divider orientation="vertical" sx={{ display: 'inline' }} />
          </Grid>
          <Grid>
            <Typography color="textSecondary" fontWeight={500}>
              {billingProfile}
            </Typography>
          </Grid>
          {businessUnitName && (
            <>
              <Grid>
                <Divider orientation="vertical" sx={{ display: 'inline' }} />
              </Grid>
              <Grid>
                <Typography color="textSecondary" fontWeight={500}>
                  {businessUnitName}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid>
        <Grid container alignItems="center" mt={1}>
          <InteractiveTagsList
            disableLocation
            disableService
            accountId={accountId}
            data={{
              accountId,
              name: accountName,
              tagDetails: { forAccount: tags },
            }}
            onSave={handleTagsSave}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

CustomerInfo.propTypes = {}

export default CustomerInfo
